import classNames from 'classnames';
import React from 'react';

import {GwgAccelerator} from '@udemy/career-academies';
import {ContentArea} from '@udemy/react-layout-components';

import {GoogleTracker} from '../google-tracker/google-tracker.react-component';
import {LearningPathCourse} from './components/learning-path-course.react-component';
import {OrderBar} from './components/order-bar.react-component';
import styles from './google-learning-path.module.less';
import {deriveStepStatus} from './learning-path-state';
import {LearningPathStepStatus, LearningPathStepType} from './learning-path-status';

import type {CertificateShareLinks} from '../google-tracker/components/google-redirect-buttons.react-component';
import type {StepStatus} from './learning-path-status';

type Step = {
    id: string | number;
    type: LearningPathStepType;
    status?: StepStatus;
    courseTitle?: string;
    durationMinutes?: number;
    tags?: string[];
    ratingAverage?: number;
    ratingCount?: number;
    progressPercent?: number;
    thumbnailUrl?: string;
    cta?: {
        href?: string;
    };
    data?: {
        summary?: string;
    };
    details?: {
        title?: string;
        keyPoints?: string[];
    };
};

interface GoogleLearningPathProps {
    steps: Step[];
    trackerCertificate?: CertificateShareLinks;
    isAcceleratorEnrolled?: boolean;
    certificateTitle: string;
    certificateInfo?: GwgAccelerator['certificateInfo'] | null;
}

export function GoogleLearningPath({
    steps,
    trackerCertificate,
    isAcceleratorEnrolled = true,
    certificateTitle,
    certificateInfo,
}: GoogleLearningPathProps) {
    const courseSteps = Array.isArray(steps) ? steps : [];
    const totalCourses = courseSteps.length;

    const completedCourses = courseSteps.filter(
        (step) => deriveStepStatus(step) === LearningPathStepStatus.Completed,
    ).length;
    const allCoursesCompleted = courseSteps.every(
        (step) => deriveStepStatus(step) === LearningPathStepStatus.Completed,
    );
    const firstIncompleteCourseIndex = courseSteps.findIndex(
        (step) => deriveStepStatus(step) !== LearningPathStepStatus.Completed,
    );

    const allSteps: Step[] = [
        ...courseSteps,
        {
            id: courseSteps.length + 1,
            type: LearningPathStepType.Tracker,
            status:
                isAcceleratorEnrolled && allCoursesCompleted
                    ? LearningPathStepStatus.Completed
                    : LearningPathStepStatus.InProgress,
        },
    ];

    return (
        <div className={styles['section-parent-container']}>
            <ContentArea>
                <div className={styles['google-learning-path-container']}>
                    <div className={styles['order-bar-stack']}>
                        {allSteps.map((step: Step, index: number) => {
                            const status = deriveStepStatus(step);
                            const isTracker = step.type === LearningPathStepType.Tracker;

                            return (
                                <div key={step.id} className={styles['order-bar-row']}>
                                    <div className={styles['node-column']}>
                                        <div
                                            className={classNames(
                                                styles['line-segment'],
                                                styles['line-top'],
                                                {
                                                    [styles['line-hidden']]: index === 0,
                                                },
                                            )}
                                        />
                                        <OrderBar
                                            // Display index is 1-based and now includes the tracker node.
                                            displayIndex={index + 1}
                                            status={status}
                                            type={step.type}
                                            removeMargin={true}
                                        />
                                        <div
                                            className={classNames(
                                                styles['line-segment'],
                                                styles['line-bottom'],
                                                {
                                                    [styles['line-hidden']]:
                                                        index === allSteps.length - 1,
                                                },
                                            )}
                                        />
                                    </div>
                                    <div className={styles['order-bar-course']}>
                                        {isTracker ? (
                                            <GoogleTracker
                                                isAcceleratorEnrolled={isAcceleratorEnrolled}
                                                totalCourses={totalCourses}
                                                completedCourses={completedCourses}
                                                certificate={trackerCertificate}
                                                certificateTitle={certificateTitle}
                                                certificateInfo={certificateInfo}
                                            />
                                        ) : (
                                            <LearningPathCourse
                                                course={step}
                                                status={status}
                                                courseNumber={index + 1}
                                                totalCourses={totalCourses}
                                                isFirstIncompleteCourse={
                                                    index === firstIncompleteCourseIndex
                                                }
                                                isEnrolled={isAcceleratorEnrolled}
                                                defaultExpanded={index === 0}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </ContentArea>
        </div>
    );
}

export type GoogleLearningPathEnrolledProps = Omit<
    GoogleLearningPathProps,
    'isAcceleratorEnrolled'
>;
export function GoogleLearningPathEnrolled(props: GoogleLearningPathEnrolledProps) {
    return <GoogleLearningPath {...props} isAcceleratorEnrolled={true} />;
}

export type GoogleLearningPathPreEnrolledProps = Omit<
    GoogleLearningPathProps,
    'isAcceleratorEnrolled'
>;
export function GoogleLearningPathPreEnrolled(props: GoogleLearningPathPreEnrolledProps) {
    return <GoogleLearningPath {...props} isAcceleratorEnrolled={false} />;
}
